import React, { useState, useCallback, useEffect, useRef } from 'react'
import { Input, Button, Select, Space, Tag, Form } from 'antd'
import { MenuOutlined } from '@ant-design/icons'

const { Option } = Select

interface TemplatePart {
  type: 'sequence' | 'date' | 'prefix' | 'postfix'
  value?: string
  length?: number
  id: string
}

interface ContractNumberGeneratorProps {
  initialTemplate?: string
  onTemplateChange?: (template: string) => void
  sequenceNumber?: number
}

const ContractNumberGenerator: React.FC<ContractNumberGeneratorProps> = ({
                                                                           initialTemplate = '',
                                                                           onTemplateChange,
                                                                           sequenceNumber = Math.floor(Math.random()*10000)
                                                                         }) => {
  const [form] = Form.useForm()
  const [templateParts, setTemplateParts] = useState<TemplatePart[]>([])
  const [prefixValue, setPrefixValue] = useState('')
  const [postfixValue, setPostfixValue] = useState('')
  const [sequenceLength, setSequenceLength] = useState<number | undefined>(undefined)
  const [separatorValue, setSeparatorValue] = useState('')
  const [draggedItem, setDraggedItem] = useState<string | null>(null)
  const [showPrefixInput, setShowPrefixInput] = useState(false)
  const [showPostfixInput, setShowPostfixInput] = useState(false)
  const [showSequenceInput, setShowSequenceInput] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const lastTemplateRef = useRef<string>('')
  const initialTemplateRef = useRef<string>(initialTemplate)

  // Функция для парсинга строки шаблона
  const parseTemplateString = useCallback((templateString: string) => {
    const parts: TemplatePart[] = []
    let currentPrefix = ''
    let currentPostfix = ''
    let currentSequenceLength: number | undefined = undefined
    let separator = ''

    const markerRegex = /\[(SEQ(:(\d+))?|DATE|PREFIX:([^\]]*?)|POSTFIX:([^\]]*?))\](?=[_\-/]?|$)/g
    let lastIndex = 0
    const separators: { [key: string]: number } = {}
    let match

    while ((match = markerRegex.exec(templateString)) !== null) {
      if (lastIndex < match.index) {
        const separatorCandidate = templateString.substring(lastIndex, match.index)
        if (separatorCandidate) {
          separators[separatorCandidate] = (separators[separatorCandidate] || 0) + 1
        }
      }
      lastIndex = match.index + match[0].length

      const markerType = match[1]
      if (markerType.startsWith('SEQ')) {
        const length = match[3] ? parseInt(match[3], 10) : undefined
        currentSequenceLength = length
        parts.push({
          type: 'sequence',
          length,
          id: `sequence-${Date.now()}-${Math.random()}`
        })
      } else if (markerType === 'DATE') {
        parts.push({
          type: 'date',
          id: `date-${Date.now()}-${Math.random()}`
        })
      } else if (markerType.startsWith('PREFIX:')) {
        const value = match[4] || ''
        currentPrefix = value
        if (value) {
          parts.push({
            type: 'prefix',
            value,
            id: `prefix-${Date.now()}-${Math.random()}`
          })
        }
      } else if (markerType.startsWith('POSTFIX:')) {
        const value = match[5] || ''
        currentPostfix = value
        if (value) {
          parts.push({
            type: 'postfix',
            value,
            id: `postfix-${Date.now()}-${Math.random()}`
          })
        }
      }

      const nextChar = templateString[lastIndex]
      if (nextChar && ['_', '-', '/'].includes(nextChar)) {
        separators[nextChar] = (separators[nextChar] || 0) + 1
        lastIndex++
      }
    }

    let mostCommonSeparator = ''
    let maxCount = 0
    for (const [sep, count] of Object.entries(separators)) {
      if (count > maxCount) {
        mostCommonSeparator = sep
        maxCount = count
      }
    }

    separator = mostCommonSeparator || ''

    return { parts, separator, prefix: currentPrefix, postfix: currentPostfix, sequenceLength: currentSequenceLength }
  }, [])

  const generateTemplateString = useCallback((parts: TemplatePart[], separator: string) => {
    const partsValues = parts.map(part => {
      switch (part.type) {
        case 'sequence':
          return part.length ? `[SEQ:${part.length}]` : '[SEQ]'
        case 'date':
          return '[DATE]'
        case 'prefix':
          return `[PREFIX:${part.value || ''}]`
        case 'postfix':
          return `[POSTFIX:${part.value || ''}]`
        default:
          return ''
      }
    })

    return partsValues.join(separator)
  }, [])

  // Генерация шаблонизированного номера
  const generateFormattedNumber = useCallback(() => {
    const currentDate = new Date()
    const formattedDate = `${currentDate.getFullYear()}${(currentDate.getMonth() + 1).toString().padStart(2, '0')}${currentDate.getDate().toString().padStart(2, '0')}`

    const partsValues = templateParts.map(part => {
      switch (part.type) {
        case 'sequence':
          return part.length ? sequenceNumber.toString().padStart(part.length, '0') : sequenceNumber.toString()
        case 'date':
          return formattedDate
        case 'prefix':
          return part.value || ''
        case 'postfix':
          return part.value || ''
        default:
          return ''
      }
    })

    return partsValues.join(separatorValue)
  }, [templateParts, separatorValue, sequenceNumber])

  // Инициализация компонента
  useEffect(() => {
    if (!initialTemplateRef.current && !isInitialized) {
      setIsInitialized(true)
      return
    }
    if (initialTemplateRef.current && !isInitialized) {
      const { parts, separator, prefix, postfix, sequenceLength } = parseTemplateString(initialTemplateRef.current)
      setTemplateParts(parts)
      setSeparatorValue(separator)
      setPrefixValue(prefix)
      setPostfixValue(postfix)
      setSequenceLength(sequenceLength)
      form.setFieldsValue({ prefix, postfix, sequenceLength: sequenceLength?.toString() })
      if (prefix) setShowPrefixInput(true)
      if (postfix) setShowPostfixInput(true)
      const hasSequence = parts.some(p => p.type === 'sequence')
      if (hasSequence) {
        setShowSequenceInput(true)
      }
      const initialTemplateString = generateTemplateString(parts, separator)
      lastTemplateRef.current = initialTemplateString
      if (onTemplateChange) {
        onTemplateChange(initialTemplateString)
      }
      setIsInitialized(true)
    }
  }, [isInitialized, parseTemplateString, form, generateTemplateString, onTemplateChange])

  // Эффект для добавления/удаления маркера префикса
  useEffect(() => {
    if (isInitialized) {
      if (showPrefixInput && prefixValue) {
        setTemplateParts(prev => {
          const existingIndex = prev.findIndex(part => part.type === 'prefix')
          if (existingIndex >= 0) {
            return prev.map(part =>
              part.type === 'prefix' ? { ...part, value: prefixValue } : part
            )
          } else {
            return [...prev, {
              type: 'prefix',
              value: prefixValue,
              id: `prefix-${Date.now()}-${Math.random()}`
            }]
          }
        })
      } else if (!prefixValue) {
        setTemplateParts(prev => prev.filter(part => part.type !== 'prefix'))
      }
    }
  }, [showPrefixInput, prefixValue, isInitialized])

  // Эффект для добавления/удаления маркера постфикса
  useEffect(() => {
    if (isInitialized) {
      if (showPostfixInput && postfixValue) {
        setTemplateParts(prev => {
          const existingIndex = prev.findIndex(part => part.type === 'postfix')
          if (existingIndex >= 0) {
            return prev.map(part =>
              part.type === 'postfix' ? { ...part, value: postfixValue } : part
            )
          } else {
            return [...prev, {
              type: 'postfix',
              value: postfixValue,
              id: `postfix-${Date.now()}-${Math.random()}`
            }]
          }
        })
      } else if (!postfixValue) {
        setTemplateParts(prev => prev.filter(part => part.type !== 'postfix'))
      }
    }
  }, [showPostfixInput, postfixValue, isInitialized])

  // Эффект для обновления длины последовательности
  useEffect(() => {
    if (isInitialized && showSequenceInput) {
      setTemplateParts(prev => {
        const existingIndex = prev.findIndex(part => part.type === 'sequence')
        if (existingIndex >= 0) {
          return prev.map(part =>
            part.type === 'sequence' ? { ...part, length: sequenceLength } : part
          )
        }
        return prev
      })
    }
  }, [showSequenceInput, sequenceLength, isInitialized])

  const toggleTemplatePart = useCallback((type: 'sequence' | 'date') => {
    setTemplateParts(prev => {
      const existingIndex = prev.findIndex(part => part.type === type)
      if (existingIndex >= 0) {
        const newParts = prev.filter(part => part.type !== type)
        if (newParts.length < 2) {
          setSeparatorValue('')
        }
        if (type === 'sequence') {
          setShowSequenceInput(false)
          setSequenceLength(undefined)
          form.setFieldsValue({ sequenceLength: '' })
        }
        return newParts
      } else {
        const newParts = [...prev, {
          type,
          id: `${type}-${Date.now()}-${Math.random()}`
        }]
        if (type === 'sequence') {
          setShowSequenceInput(true)
        }
        return newParts
      }
    })
  }, [])

  const handlePrefixToggle = useCallback(() => {
    const newShow = !showPrefixInput
    if (!newShow) {
      setPrefixValue('')
      form.setFieldsValue({ prefix: '' })
    }
    setShowPrefixInput(newShow)
  }, [showPrefixInput])

  const handlePostfixToggle = useCallback(() => {
    const newShow = !showPostfixInput
    if (!newShow) {
      setPostfixValue('')
      form.setFieldsValue({ postfix: '' })
    }
    setShowPostfixInput(newShow)
  }, [showPostfixInput])

  // Вызываем callback при изменении шаблона
  useEffect(() => {
    if (isInitialized) {
      const newTemplate = generateTemplateString(templateParts, separatorValue)
      if (newTemplate !== lastTemplateRef.current) {
        lastTemplateRef.current = newTemplate
        if (onTemplateChange) {
          onTemplateChange(newTemplate)
        }
      }
    }
  }, [templateParts, separatorValue, isInitialized, generateTemplateString, onTemplateChange])

  const isActive = useCallback((type: TemplatePart['type']) => {
    if (type === 'prefix') return showPrefixInput
    if (type === 'postfix') return showPostfixInput
    if (type === 'sequence') return showSequenceInput
    return templateParts.some(part => part.type === type)
  }, [templateParts, showPrefixInput, showPostfixInput, showSequenceInput])

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, id: string) => {
    e.dataTransfer.setData('text/plain', id)
    setDraggedItem(id)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetIndex: number) => {
    e.preventDefault()
    const sourceId = e.dataTransfer.getData('text/plain')
    if (!sourceId) {
      setDraggedItem(null)
      return
    }
    setTemplateParts(prev => {
      const sourceIndex = prev.findIndex(item => item.id === sourceId)
      if (sourceIndex === -1 || sourceIndex === targetIndex) {
        setDraggedItem(null)
        return prev
      }
      const newItems = [...prev]
      const [movedItem] = newItems.splice(sourceIndex, 1)
      newItems.splice(targetIndex, 0, movedItem)
      return newItems
    })
    setDraggedItem(null)
  }

  const handleDragEnd = () => {
    setDraggedItem(null)
  }

  const getPartDisplay = (part: TemplatePart) => {
    switch (part.type) {
      case 'sequence':
        return part.length ? `[SEQ:${part.length}]` : '[SEQ]'
      case 'date':
        return '[DATE]'
      case 'prefix':
        return `[PREFIX:${part.value || ''}]`
      case 'postfix':
        return `[POSTFIX:${part.value || ''}]`
      default:
        return ''
    }
  }

  return (
    <div style={{ padding: 0, maxWidth: 600 }}>
      <Space direction='vertical' size='middle' style={{ width: '100%' }}>
        {/* Поле для отображения итогового шаблона */}
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'baseline',
          }}
        >
          <span>Шаблон:</span>
          <div
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: '3px',
              padding: '4px 11px',
              minHeight: '36px',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '4px',
              background: '#f5f5f5',
              cursor: 'text',
              flex: 1,
            }}
            onDragOver={handleDragOver}
            onDrop={e => handleDrop(e, templateParts.length)}
          >
            {templateParts.length === 0 ? (
              <span style={{ color: '#bfbfbf' }}>Шаблон номера договора</span>
            ) : (
              templateParts.map((part, index) => (
                <React.Fragment key={part.id}>
                  <div
                    draggable
                    onDragStart={e => handleDragStart(e, part.id)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={e => handleDrop(e, index)}
                    style={{
                      opacity: draggedItem === part.id ? 0.5 : 1,
                      cursor: 'grab',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Tag
                      style={{
                        padding: '2px 6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        margin: 0,
                      }}
                    >
                      <MenuOutlined style={{ fontSize: 12 }} />
                      {getPartDisplay(part)}
                    </Tag>
                  </div>
                  {index < templateParts.length - 1 && separatorValue && (
                    <span style={{ color: '#999' }}>{separatorValue}</span>
                  )}
                </React.Fragment>
              ))
            )}
          </div>
        </div>
        {/* Поле для отображения шаблонизированного номера */}

        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'baseline',
          }}
        >
          <span>Пример:</span>
          <span style={{ color: templateParts.length === 0 ? '#bfbfbf' : '#000', userSelect: 'none' }}>
            {templateParts.length === 0 ? 'Пример номера договора' : generateFormattedNumber()}
          </span>
        </div>
        {templateParts.length ? (<div>С последовательностью: <b>{sequenceNumber}</b></div>) : null}

        {/* Кнопки для добавления/удаления элементов шаблона */}
          <Space wrap>
            <Button
              size='small'
              type={isActive('sequence') ? 'primary' : 'default'}
              onClick={() => toggleTemplatePart('sequence')}
            >
              Последовательность
            </Button>
            <Button
              size='small'
              type={isActive('date') ? 'primary' : 'default'}
              onClick={() => toggleTemplatePart('date')}
            >
              Дата
            </Button>
            <Button
              size='small'
              type={showPrefixInput ? 'primary' : 'default'}
              onClick={handlePrefixToggle}
            >
              Префикс
            </Button>
            <Button
              size='small'
              type={showPostfixInput ? 'primary' : 'default'}
              onClick={handlePostfixToggle}
            >
              Постфикс
            </Button>
            <Button
              size='small'
              disabled={templateParts.length < 2}
              type={separatorValue ? 'primary' : 'default'}
              onClick={() => setSeparatorValue(separatorValue ? '' : '-')}
            >
              Разделитель
            </Button>
          </Space>

          {/* Поля для настройки активных элементов */}
          <Form
            form={form}
            size='small'
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 12 }}
          >
            {showSequenceInput && (
              <Form.Item
                name='sequenceLength'
                label='Кол-во символов'
                rules={[
                  { pattern: /^\d+$/, message: 'Только положительные числа' }
                ]}
              >
                <Input
                  value={sequenceLength}
                  onChange={e => {
                    const value = e.target.value
                    if (/^\d*$/.test(value)) {
                      setSequenceLength(value ? parseInt(value, 10) : undefined)
                    }
                  }}
                  placeholder='Например, 4'
                  style={{ width: 200 }}
                />
              </Form.Item>
            )}
            {showPrefixInput && (
              <Form.Item
                name='prefix'
                label='Префикс'
              >
                <Input
                  value={prefixValue}
                  onChange={e => setPrefixValue(e.target.value)}
                  placeholder='Введите префикс'
                  style={{ width: 200 }}
                />
              </Form.Item>
            )}
            {showPostfixInput && (
              <Form.Item
                name='postfix'
                label='Постфикс'
              >
                <Input
                  value={postfixValue}
                  onChange={e => setPostfixValue(e.target.value)}
                  placeholder='Введите постфикс'
                  style={{ width: 200 }}
                />
              </Form.Item>
            )}
            {separatorValue && (
              <Form.Item label='Разделитель'>
                <Select
                  value={separatorValue}
                  onChange={setSeparatorValue}
                  style={{ width: 80 }}
                >
                  <Option value='/'>/</Option>
                  <Option value='_'>_</Option>
                  <Option value='-'>-</Option>
                </Select>
              </Form.Item>
            )}
          </Form>
      </Space>
    </div>
)
}

export default ContractNumberGenerator
